import React, { useMemo, useState, useEffect } from 'react';
import { Card, Row, Col, Typography, DatePicker, Input, Space, Select, Drawer, Form, InputNumber, message } from 'antd';
import dayjs from 'dayjs';
import type { ColumnsType } from 'antd/es/table';
import StandardTable from '../../../components/common/StandardTable';
import { useGetMatGapDeliveriesTemporary } from '../../../hooks/genericHooks';
import type { MatGapDeliveriesTemporary } from '../../../types';
import MatGapMasterMaterialSelect from '../../../components/modals/MatGapMasterMaterialSelect';
import { updateData } from '../../../services/api';
import { formatNumberSmart } from '../../../utils/numberFormat';
import Button from '../../../components/common/Button';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

export default function MatGapPreDelivery() {
  const { data = [], isLoading, refetch } = useGetMatGapDeliveriesTemporary() as any;
  const deliveries: MatGapDeliveriesTemporary[] = Array.isArray(data) ? data : (data?.data ?? []);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingRow, setEditingRow] = useState<MatGapDeliveriesTemporary | null>(null);
  const [saving, setSaving] = useState(false);
  const [matModalOpen, setMatModalOpen] = useState(false);
  const [form] = Form.useForm();

  // Local UI filters
  const [matIdSearch, setMatIdSearch] = useState<string>('');
  const [search, setSearch] = useState<string>('');
  const [shippedFromSearch, setShippedFromSearch] = useState<string>('');
  const [notesSearch, setNotesSearch] = useState<string>('');
  const [dateRange, setDateRange] = useState<[string | null, string | null]>([null, null]);
  const [shipmentSel, setShipmentSel] = useState<string>('');
  const [groupLongSel, setGroupLongSel] = useState<string>('');
  const [hierLongSel, setHierLongSel] = useState<string>('');
  const [extGroupSel, setExtGroupSel] = useState<string>('');

  const norm = (s: any) => (s == null ? '' : String(s)).trim();

  // Cascaded options
  const shipmentOptions = useMemo(() => {
    const byKey = new Map<string, string>();
    (deliveries || [])
      .filter(r => !groupLongSel || norm(r.mat_group_long).toLowerCase() === norm(groupLongSel).toLowerCase())
      .filter(r => !hierLongSel || norm(r.hierarchy_long).toLowerCase() === norm(hierLongSel).toLowerCase())
      .filter(r => !extGroupSel || norm((r as any).ext_group).toLowerCase() === norm(extGroupSel).toLowerCase())
      .forEach(r => {
        const raw = r?.shipment_id == null ? '' : String((r as any).shipment_id);
        if (!raw) return;
        const k = raw.toLowerCase();
        if (!byKey.has(k)) byKey.set(k, raw);
      });
    return Array.from(byKey.values()).sort((a, b) => a.localeCompare(b)).map(v => ({ value: v, label: v }));
  }, [deliveries, groupLongSel, hierLongSel, extGroupSel]);

  const groupLongOptions = useMemo(() => {
    const byKey = new Map<string, string>();
    (deliveries || [])
      .filter(r => !shipmentSel || String((r as any).shipment_id || '') === shipmentSel)
      .filter(r => !hierLongSel || norm(r.hierarchy_long).toLowerCase() === norm(hierLongSel).toLowerCase())
      .filter(r => !extGroupSel || norm((r as any).ext_group).toLowerCase() === norm(extGroupSel).toLowerCase())
      .forEach(r => {
        const raw = norm(r.mat_group_long);
        if (!raw) return;
        const k = raw.toLowerCase();
        if (!byKey.has(k)) byKey.set(k, raw);
      });
    return Array.from(byKey.values()).sort((a, b) => a.localeCompare(b)).map(v => ({ value: v, label: v }));
  }, [deliveries, shipmentSel, hierLongSel, extGroupSel]);

  const hierLongOptions = useMemo(() => {
    const byKey = new Map<string, string>();
    (deliveries || [])
      .filter(r => !shipmentSel || String((r as any).shipment_id || '') === shipmentSel)
      .filter(r => !groupLongSel || norm(r.mat_group_long).toLowerCase() === norm(groupLongSel).toLowerCase())
      .filter(r => !extGroupSel || norm((r as any).ext_group).toLowerCase() === norm(extGroupSel).toLowerCase())
      .forEach(r => {
        const raw = norm(r.hierarchy_long);
        if (!raw) return;
        const k = raw.toLowerCase();
        if (!byKey.has(k)) byKey.set(k, raw);
      });
    return Array.from(byKey.values()).sort((a, b) => a.localeCompare(b)).map(v => ({ value: v, label: v }));
  }, [deliveries, shipmentSel, groupLongSel, extGroupSel]);

  const extGroupOptions = useMemo(() => {
    const byKey = new Map<string, string>();
    (deliveries || [])
      .filter(r => !shipmentSel || String((r as any).shipment_id || '') === shipmentSel)
      .filter(r => !groupLongSel || norm(r.mat_group_long).toLowerCase() === norm(groupLongSel).toLowerCase())
      .filter(r => !hierLongSel || norm(r.hierarchy_long).toLowerCase() === norm(hierLongSel).toLowerCase())
      .forEach(r => {
        const raw = norm((r as any).ext_group);
        if (!raw) return;
        const k = raw.toLowerCase();
        if (!byKey.has(k)) byKey.set(k, raw);
      });
    return Array.from(byKey.values()).sort((a, b) => a.localeCompare(b)).map(v => ({ value: v, label: v }));
  }, [deliveries, shipmentSel, groupLongSel, hierLongSel]);

  const filteredData = useMemo(() => {
    const [start, end] = dateRange;
    const matIdTokens = matIdSearch
      .trim()
      .toLowerCase()
      .split(/\s+/)
      .filter(Boolean);
    const descTokens = search
      .trim()
      .toLowerCase()
      .split(/\s+/)
      .filter(Boolean);
    const shippedFromTokens = shippedFromSearch
      .trim()
      .toLowerCase()
      .split(/\s+/)
      .filter(Boolean);
    const notesTokens = notesSearch
      .trim()
      .toLowerCase()
      .split(/\s+/)
      .filter(Boolean);
    return (deliveries || []).filter((rec) => {
      // Material Code/ID search
      if (matIdTokens.length) {
        const hay = String((rec as any).mat_id || '').toLowerCase();
        const matchesAll = matIdTokens.every((t) => hay.includes(t));
        if (!matchesAll) return false;
      }

      // Main search: material description only
      if (descTokens.length) {
        const hay = String(rec.mat_desc || '').toLowerCase();
        const matchesAll = descTokens.every((t) => hay.includes(t));
        if (!matchesAll) return false;
      }

      // Separate filters
      if (shippedFromTokens.length) {
        const hay = String((rec as any).shipped_from || '').toLowerCase();
        const matchesAll = shippedFromTokens.every((t) => hay.includes(t));
        if (!matchesAll) return false;
      }
      if (notesTokens.length) {
        const hay = String((rec as any).notes || '').toLowerCase();
        const matchesAll = notesTokens.every((t) => hay.includes(t));
        if (!matchesAll) return false;
      }

      // dropdown filters (exact match on selected values)
      if (shipmentSel && String((rec as any).shipment_id || '') !== shipmentSel) return false;
      if (groupLongSel && norm(rec.mat_group_long).toLowerCase() !== norm(groupLongSel).toLowerCase()) return false;
      if (hierLongSel && norm(rec.hierarchy_long).toLowerCase() !== norm(hierLongSel).toLowerCase()) return false;
      if (extGroupSel && norm((rec as any).ext_group).toLowerCase() !== norm(extGroupSel).toLowerCase()) return false;
      // date range filter (inclusive)
      if (start || end) {
        const d = rec.delivery_date ? new Date(rec.delivery_date) : null;
        if (!d) return false;
        if (start) {
          const ds = new Date(start);
          if (d < new Date(ds.getFullYear(), ds.getMonth(), ds.getDate())) return false;
        }
        if (end) {
          const de = new Date(end);
          if (d > new Date(de.getFullYear(), de.getMonth(), de.getDate(), 23, 59, 59, 999)) return false;
        }
      }
      return true;
    });
  }, [deliveries, matIdSearch, search, shippedFromSearch, notesSearch, dateRange, shipmentSel, groupLongSel, hierLongSel, extGroupSel]);

  // Auto-clear invalid selections when options narrow
  useEffect(() => {
    if (shipmentSel && !shipmentOptions.some(o => (o.value as string) === shipmentSel)) setShipmentSel('');
  }, [shipmentOptions, shipmentSel]);
  useEffect(() => {
    if (groupLongSel && !groupLongOptions.some(o => (o.value as string) === groupLongSel)) setGroupLongSel('');
  }, [groupLongOptions, groupLongSel]);
  useEffect(() => {
    if (hierLongSel && !hierLongOptions.some(o => (o.value as string) === hierLongSel)) setHierLongSel('');
  }, [hierLongOptions, hierLongSel]);
  useEffect(() => {
    if (extGroupSel && !extGroupOptions.some(o => (o.value as string) === extGroupSel)) setExtGroupSel('');
  }, [extGroupOptions, extGroupSel]);

  const columns: any[] = useMemo(() => [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 80, sorter: (a: MatGapDeliveriesTemporary, b: MatGapDeliveriesTemporary) => (a.id ?? 0) - (b.id ?? 0) },
    { title: 'Shipment ID', dataIndex: 'shipment_id', key: 'shipment_id', width: 140, sorter: (a: MatGapDeliveriesTemporary, b: MatGapDeliveriesTemporary) => String(a.shipment_id || '').localeCompare(String(b.shipment_id || '')) },
    { title: 'Material ID', dataIndex: 'mat_id', key: 'mat_id', width: 140, sorter: (a: MatGapDeliveriesTemporary, b: MatGapDeliveriesTemporary) => String(a.mat_id || '').localeCompare(String(b.mat_id || '')) },
    { title: 'Description', dataIndex: 'mat_desc', key: 'mat_desc', ellipsis: true, width: 260, sorter: (a: MatGapDeliveriesTemporary, b: MatGapDeliveriesTemporary) => (a.mat_desc || '').localeCompare(b.mat_desc || '') },
    { title: 'Unit', dataIndex: 'unit', key: 'unit', width: 90, sorter: (a: MatGapDeliveriesTemporary, b: MatGapDeliveriesTemporary) => (a.unit || '').localeCompare(b.unit || '') },
    { title: 'Group', dataIndex: 'mat_group', key: 'mat_group', width: 140, sorter: (a: MatGapDeliveriesTemporary, b: MatGapDeliveriesTemporary) => (a.mat_group || '').localeCompare(b.mat_group || '') },
    { title: 'Group (Long)', dataIndex: 'mat_group_long', key: 'mat_group_long', width: 220, ellipsis: true, sorter: (a: MatGapDeliveriesTemporary, b: MatGapDeliveriesTemporary) => (a.mat_group_long || '').localeCompare(b.mat_group_long || '') },
    { title: 'Hierarchy', dataIndex: 'hierarchy', key: 'hierarchy', width: 160, sorter: (a: MatGapDeliveriesTemporary, b: MatGapDeliveriesTemporary) => (a.hierarchy || '').localeCompare(b.hierarchy || '') },
    { title: 'Hierarchy (Long)', dataIndex: 'hierarchy_long', key: 'hierarchy_long', width: 240, ellipsis: true, sorter: (a: MatGapDeliveriesTemporary, b: MatGapDeliveriesTemporary) => (a.hierarchy_long || '').localeCompare(b.hierarchy_long || '') },
    { title: 'Ext Group', dataIndex: 'ext_group', key: 'ext_group', width: 160, sorter: (a: MatGapDeliveriesTemporary, b: MatGapDeliveriesTemporary) => String(a.ext_group || '').localeCompare(String(b.ext_group || '')) },
    { title: 'Qty', dataIndex: 'qty', key: 'qty', width: 120, align: 'right', render: (v: unknown) => formatNumberSmart(v), sorter: (a: MatGapDeliveriesTemporary, b: MatGapDeliveriesTemporary) => (a.qty ?? 0) - (b.qty ?? 0) },
    {
      title: 'Delivery Date', dataIndex: 'delivery_date', key: 'delivery_date', width: 160,
      render: (v: string) => v ? dayjs(v).format('YYYY-MM-DD') : '-',
      sorter: (a: MatGapDeliveriesTemporary, b: MatGapDeliveriesTemporary) => new Date(a.delivery_date || 0).getTime() - new Date(b.delivery_date || 0).getTime(),
      exportValue: (r: MatGapDeliveriesTemporary) => r.delivery_date ? dayjs(r.delivery_date).format('YYYY-MM-DD') : ''
    },
    { title: 'SAT No', dataIndex: 'sat_no', key: 'sat_no', width: 140, sorter: (a: MatGapDeliveriesTemporary, b: MatGapDeliveriesTemporary) => (a.sat_no || '').localeCompare(b.sat_no || '') },
    { title: 'SAS No', dataIndex: 'sas_no', key: 'sas_no', width: 140, sorter: (a: MatGapDeliveriesTemporary, b: MatGapDeliveriesTemporary) => (a.sas_no || '').localeCompare(b.sas_no || '') },
    { title: 'Shipped From', dataIndex: 'shipped_from', key: 'shipped_from', width: 160, ellipsis: true, sorter: (a: MatGapDeliveriesTemporary, b: MatGapDeliveriesTemporary) => String(a.shipped_from || '').localeCompare(String(b.shipped_from || '')) },
    { title: 'Notes', dataIndex: 'notes', key: 'notes', width: 240, ellipsis: true, sorter: (a: MatGapDeliveriesTemporary, b: MatGapDeliveriesTemporary) => String(a.notes || '').localeCompare(String(b.notes || '')) },
  ], []);

  const openEditor = (row: MatGapDeliveriesTemporary) => {
    setEditingRow(row);
    form.setFieldsValue({
      shipment_id: row.shipment_id ?? null,
      mat_id: row.mat_id ?? null,
      mat_desc: row.mat_desc ?? null,
      unit: row.unit ?? null,
      mat_group: row.mat_group ?? null,
      mat_group_long: row.mat_group_long ?? null,
      hierarchy: row.hierarchy ?? null,
      hierarchy_long: row.hierarchy_long ?? null,
      qty: row.qty ?? null,
      delivery_date: row.delivery_date ? dayjs(row.delivery_date) : null,
      ext_group: (row as any).ext_group ?? null,
      sat_no: row.sat_no ?? null,
      sas_no: row.sas_no ?? null,
      shipped_from: (row as any).shipped_from ?? null,
      notes: (row as any).notes ?? null,
    });
    setDrawerOpen(true);
  };

  const normalizePayload = (values: any) => {
    const out: any = { ...values };
    Object.keys(out).forEach((k) => {
      if (out[k] === '') out[k] = null;
    });
    if (out.delivery_date) out.delivery_date = dayjs(out.delivery_date).format('YYYY-MM-DD');
    return out;
  };

  const handleSave = async () => {
    if (!editingRow) return;
    try {
      const values = await form.validateFields();
      const payload = normalizePayload(values);
      setSaving(true);
      await updateData('mat_gap_deliveries_temporary', editingRow.id, payload);
      message.success('Row updated');
      setDrawerOpen(false);
      setEditingRow(null);
      form.resetFields();
      await refetch?.();
    } catch (e: any) {
      const backendMsg =
        e?.response?.data?.message ||
        e?.response?.data?.error ||
        e?.message;
      if (backendMsg) message.error(String(backendMsg));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card bordered={false} bodyStyle={{ padding: 8 }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 12 }}>
        <Col>
          <Title level={4} style={{ margin: 0 }}>GAP Pre-Delivery</Title>
          <Text type="secondary">Baseline list (to be refined)</Text>
        </Col>
        <Col>
          <Space size={6} direction="vertical" style={{ width: '100%' }}>
            <Space size={8} wrap>
              <Input
                placeholder="Material Code/ID"
                allowClear
                size="small"
                value={matIdSearch}
                onChange={(e) => setMatIdSearch(e.target.value)}
                style={{ width: 160 }}
              />
              <Select
                placeholder="Shipment ID"
                allowClear
                showSearch
                size="small"
                value={shipmentSel || undefined}
                onChange={(v) => setShipmentSel(v || '')}
                options={shipmentOptions}
                style={{ width: 140 }}
                filterOption={(input, option) => ((option?.label as string) || '').toLowerCase().includes((input || '').toLowerCase())}
              />
              <Select
                placeholder="Group (Long)"
                allowClear
                showSearch
                size="small"
                value={groupLongSel || undefined}
                onChange={(v) => setGroupLongSel(v || '')}
                options={groupLongOptions}
                style={{ width: 180 }}
                filterOption={(input, option) => ((option?.label as string) || '').toLowerCase().includes((input || '').toLowerCase())}
              />
              <Select
                placeholder="Hierarchy (Long)"
                allowClear
                showSearch
                size="small"
                value={hierLongSel || undefined}
                onChange={(v) => setHierLongSel(v || '')}
                options={hierLongOptions}
                style={{ width: 180 }}
                filterOption={(input, option) => ((option?.label as string) || '').toLowerCase().includes((input || '').toLowerCase())}
              />
              <Select
                placeholder="Ext Group"
                allowClear
                showSearch
                size="small"
                value={extGroupSel || undefined}
                onChange={(v) => setExtGroupSel(v || '')}
                options={extGroupOptions}
                style={{ width: 160 }}
                filterOption={(input, option) => ((option?.label as string) || '').toLowerCase().includes((input || '').toLowerCase())}
              />
              <Input
                placeholder="Search description..."
                allowClear
                size="small"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{ width: 180 }}
              />
              <Input
                placeholder="Shipped From"
                allowClear
                size="small"
                value={shippedFromSearch}
                onChange={(e) => setShippedFromSearch(e.target.value)}
                style={{ width: 160 }}
              />
              <Input
                placeholder="Notes"                allowClear
                size="small"
                value={notesSearch}
                onChange={(e) => setNotesSearch(e.target.value)}
                style={{ width: 160 }}
              />
    
              <RangePicker
                size="small"
                style={{ width: 220 }}
                value={[
                  dateRange[0] ? dayjs(dateRange[0]) : null,
                  dateRange[1] ? dayjs(dateRange[1]) : null,
                ]}
                onChange={(vals) => {
                  if (!vals || vals.length !== 2) { setDateRange([null, null]); return; }
                  setDateRange([
                    vals[0] ? vals[0].format('YYYY-MM-DD') : null,
                    vals[1] ? vals[1].format('YYYY-MM-DD') : null,
                  ]);
                }}
              />
              <Button
                size="small"
                onClick={() => {
                  setShipmentSel('');
                  setGroupLongSel('');
                  setHierLongSel('');
                  setExtGroupSel('');
                  setMatIdSearch('');
                  setSearch('');
                  setShippedFromSearch('');
                  setNotesSearch('');
                  setDateRange([null, null]);
                }}
              >
                Reset Filters
              </Button>
            </Space>
          </Space>
        </Col>
      </Row>
      <StandardTable<MatGapDeliveriesTemporary>
        columns={columns}
        dataSource={filteredData}
        rowKey={(r) => String(r.id)}
        loading={isLoading}
        pagination={{ pageSize: 20, showSizeChanger: true }}
        enableExport
        scroll={{ x: 'max-content' }}
        onRow={(record) => ({
          onDoubleClick: () => openEditor(record),
        })}
      />

      <Drawer
        title={editingRow ? `Edit Pre-Delivery #${editingRow.id}` : 'Edit Pre-Delivery'}
        open={drawerOpen}
        width={520}
        onClose={() => {
          setDrawerOpen(false);
          setEditingRow(null);
          form.resetFields();
        }}
        extra={
          <Space>
            <Button
              onClick={() => {
                setDrawerOpen(false);
                setEditingRow(null);
                form.resetFields();
              }}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button action="edit" type="primary" onClick={handleSave} loading={saving}>
              Save
            </Button>
          </Space>
        }
      >
        <Form form={form} layout="vertical">
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="shipment_id" label="Shipment ID">
                <Input allowClear />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Material ID" required={false}>
                <Space.Compact style={{ width: '100%' }}>
                  <Form.Item name="mat_id" noStyle>
                    <Input allowClear placeholder="Select material code" />
                  </Form.Item>
                  <Button onClick={() => setMatModalOpen(true)}>
                    Select
                  </Button>
                </Space.Compact>
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item name="mat_desc" label="Description">
                <Input allowClear />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item name="unit" label="Unit" rules={[{ required: true, message: 'Unit is required' }]}>
                <Input allowClear />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="qty" label="Qty" rules={[{ required: true, message: 'Qty is required' }]}>
                <InputNumber style={{ width: '100%' }} min={0} step={0.001} />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item name="delivery_date" label="Delivery Date" rules={[{ required: true, message: 'Delivery date is required' }]}>
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="ext_group" label="Ext Group">
                <Input allowClear />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item name="sat_no" label="SAT No">
                <Input allowClear />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="sas_no" label="SAS No">
                <Input allowClear />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item name="mat_group" label="Group">
                <Input allowClear />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="mat_group_long" label="Group (Long)">
                <Input allowClear />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item name="hierarchy" label="Hierarchy">
                <Input allowClear />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="hierarchy_long" label="Hierarchy (Long)">
                <Input allowClear />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item name="shipped_from" label="Shipped From">
                <Input allowClear />
              </Form.Item>
            </Col>
            <Col span={12} />

            <Col span={24}>
              <Form.Item name="notes" label="Notes">
                <Input.TextArea rows={3} allowClear />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Drawer>

      <MatGapMasterMaterialSelect
        open={matModalOpen}
        onCancel={() => setMatModalOpen(false)}
        onOk={(value) => {
          form.setFieldValue('mat_id', value || null);
          setMatModalOpen(false);
        }}
        title="Select Material"
        returnMode="code"
      />
    </Card>
  );
}
